import React, { useState, useEffect, useCallback } from 'react';
import { StreamingPlatform, SavedConnection } from '../types/streaming';
import PlatformIcon from '../components/streaming/PlatformIcon';
import { FILL_GRADIENT } from '../constants/gradients';
import { useStreamingPlatform } from '../hooks/useStreamingPlatform';
import PlatformSelector from '../components/streaming/PlatformSelector';
import StreamStats from '../components/streaming/StreamStats';
import LiveCommentsList from '../components/streaming/LiveCommentsList';
import SavedConnections, { saveConnection } from '../components/streaming/SavedConnections';
import { streamBroadcastService } from '../services/streaming/StreamBroadcastService';
import { useAnalytics } from '../hooks/useAnalytics';

type View = 'saved-connections' | 'platform-select' | 'username-input' | 'connected';

const platformLabels: Record<StreamingPlatform, { name: string; placeholder: string; icon: string; color: string }> = {
  tiktok: {
    name: 'TikTok',
    placeholder: 'Enter TikTok username',
    icon: '/vectors/tik-tok.svg',
    color: '#FF0050'
  },
  youtube: {
    name: 'YouTube',
    placeholder: 'Enter YouTube channel name',
    icon: '/vectors/youtube.svg',
    color: '#FF0000'
  },
  twitch: {
    name: 'Twitch',
    placeholder: 'Enter Twitch username',
    icon: '/vectors/twitch.svg',
    color: '#9146FF'
  }
};

const DISCORD_SHARE_KEY = 'beemi-share-to-discord';

export const StreamConnectorPage: React.FC = () => {
  const { trackStreamAction } = useAnalytics();
  const [view, setView] = useState<View>('saved-connections');
  const [selectedPlatform, setSelectedPlatform] = useState<StreamingPlatform | null>(null);
  const [usernameInput, setUsernameInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [hasSavedConnections, setHasSavedConnections] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [shareToDiscord, setShareToDiscord] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(DISCORD_SHARE_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  const toggleDiscordShare = () => {
    const next = !shareToDiscord;
    setShareToDiscord(next);
    try {
      localStorage.setItem(DISCORD_SHARE_KEY, String(next));
    } catch {
      // ignore
    }
  };

  const {
    isConnectedToStream,
    isConnecting,
    connectionStatus,
    connectedUsername,
    connectedPlatform,
    liveComments,
    streamingMetrics,
    roomInfo,
    connectToPlatform,
    disconnectFromPlatform
  } = useStreamingPlatform();

  // On mount: auto-reconnect from URL params, or fall back to saved connections
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlPlatform = params.get('platform') as StreamingPlatform | null;
    const urlChannel = params.get('channel');

    if (urlPlatform && urlChannel && ['tiktok', 'youtube', 'twitch'].includes(urlPlatform)) {
      setSelectedPlatform(urlPlatform);
      setUsernameInput(urlChannel);
      connectToPlatform(urlPlatform, urlChannel).catch(() => {});
      return;
    }

    try {
      const stored = localStorage.getItem('beemi-saved-connections');
      if (stored) {
        const connections = JSON.parse(stored);
        if (connections.length > 0) {
          setHasSavedConnections(true);
          setView('saved-connections');
        } else {
          setView('platform-select');
        }
      } else {
        setView('platform-select');
      }
    } catch {
      setView('platform-select');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync with connection state
  useEffect(() => {
    if (isConnectedToStream && connectedPlatform && connectedUsername) {
      const params = new URLSearchParams({ platform: connectedPlatform, channel: connectedUsername });
      window.history.replaceState(null, '', `?${params.toString()}`);
      setView('connected');
      setError(null);
    }
  }, [isConnectedToStream, connectedPlatform, connectedUsername]);

  // Update saved connection with profile picture once roomInfo arrives
  useEffect(() => {
    if (isConnectedToStream && connectedPlatform && connectedUsername && roomInfo?.streamerInfo?.profileImageUrl) {
      saveConnection(connectedPlatform, connectedUsername, roomInfo.streamerInfo.profileImageUrl);
    }
  }, [roomInfo, isConnectedToStream, connectedPlatform, connectedUsername]);

  // Respond to state requests from game windows
  useEffect(() => {
    const unsubscribe = streamBroadcastService.subscribe((event) => {
      if (event.type === 'request-state' && isConnectedToStream) {
        // Respond with current streaming state including comprehensive room info
        streamBroadcastService.respondWithState({
          connectionState: 'connected',
          platform: connectedPlatform,
          username: connectedUsername,
          metrics: streamingMetrics,
          roomInfo: roomInfo,
          title: roomInfo?.title,
          streamImageUrl: roomInfo?.streamImageUrl,
          profileImageUrl: roomInfo?.streamerInfo?.profileImageUrl
        });
      }
    });

    return () => unsubscribe();
  }, [isConnectedToStream, connectedPlatform, connectedUsername, streamingMetrics, roomInfo]);

  const handlePlatformSelect = (platform: StreamingPlatform) => {
    trackStreamAction('platform_selected', platform, {
      source: 'stream_connector'
    });
    
    setSelectedPlatform(platform);
    setUsernameInput('');
    setError(null);
    setView('username-input');
  };

  const handleSavedConnectionSelect = async (connection: SavedConnection) => {
    setSelectedPlatform(connection.platform);
    setUsernameInput(connection.username);
    setError(null);
    
    // Attempt to connect directly
    try {
      await connectToPlatform(connection.platform, connection.username);
    } catch (err: any) {
      setError(err.message || 'Connection failed');
    }
  };

  const handleAddNewConnection = () => {
    setView('platform-select');
  };

  const handleConnect = async () => {
    if (!selectedPlatform || !usernameInput.trim()) {
      setError('Please enter a username');
      return;
    }

    setError(null);
    
    trackStreamAction('stream_connect_initiated', selectedPlatform, {
      username: usernameInput.trim()
    });
    
    try {
      const success = await connectToPlatform(selectedPlatform, usernameInput.trim());
      
      if (success) {
        // Save connection for quick access later
        saveConnection(selectedPlatform, usernameInput.trim());
        
        trackStreamAction('stream_connected', selectedPlatform, {
          username: usernameInput.trim()
        });
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Connection failed';
      setError(errorMessage);
      
      trackStreamAction('stream_connection_failed', selectedPlatform, {
        username: usernameInput.trim(),
        error_message: errorMessage
      });
    }
  };

  const handleDisconnect = () => {
    if (connectedPlatform) {
      trackStreamAction('stream_disconnected', connectedPlatform, {
        username: connectedUsername
      });
    }
    disconnectFromPlatform();
    window.history.replaceState(null, '', window.location.pathname);
    setView('platform-select');
    setSelectedPlatform(null);
    setUsernameInput('');
    setError(null);
  };

  const handleToggleMinimize = useCallback(() => {
    setIsMinimized(prev => {
      const next = !prev;
      if (window.resizeTo) {
        if (next) {
          window.resizeTo(400, 280);
        } else {
          window.resizeTo(400, 600);
        }
      }
      return next;
    });
  }, []);

  const handleBack = () => {
    if (view === 'username-input') {
      if (hasSavedConnections) {
        setView('saved-connections');
      } else {
        setView('platform-select');
      }
      setSelectedPlatform(null);
      setUsernameInput('');
      setError(null);
    }
  };

  const renderSavedConnections = () => (
    <div className="p-4 h-full flex flex-col overflow-hidden">
      <div className="text-center mb-4 flex-shrink-0">
        <h1 className="text-base font-bold text-white mb-1">Connect to Stream</h1>
        <p className="text-xs text-gray-400">Select a saved connection or add a new one</p>
      </div>
      <div className="flex-1 overflow-hidden">
        <SavedConnections
          onSelect={handleSavedConnectionSelect}
          onAddNew={handleAddNewConnection}
          disabled={isConnecting}
        />
      </div>
    </div>
  );

  const renderPlatformSelect = () => (
    <div className="p-4 h-full flex flex-col overflow-hidden">
      {hasSavedConnections && (
        <button
          onClick={() => setView('saved-connections')}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors mb-3 flex-shrink-0"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>Back to Saved Connections</span>
        </button>
      )}

      <div className="text-center mb-4 flex-shrink-0">
        <h1 className="text-base font-bold text-white mb-1">Connect to Stream</h1>
        <p className="text-xs text-gray-400">Select a platform to connect to your live stream</p>
      </div>
      <div className="flex-1 overflow-y-auto pr-1">
        <PlatformSelector onSelect={handlePlatformSelect} disabled={isConnecting} />
      </div>
    </div>
  );

  const renderUsernameInput = () => {
    if (!selectedPlatform) return null;
    const platformInfo = platformLabels[selectedPlatform];

    return (
      <div className="p-4 h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center mb-4 flex-shrink-0">
          <button onClick={handleBack} className="p-1.5 -ml-1.5 mr-2 text-gray-400 hover:text-white transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <PlatformIcon platform={selectedPlatform!} className="w-5 h-5 mr-2 text-white" />
          <h1 className="text-sm font-bold text-white">{platformInfo.name}</h1>
        </div>

        {/* Username Input */}
        <div className="flex-1 overflow-y-auto pr-1">
          <label className="block text-xs font-medium text-gray-300 mb-1.5">
            {selectedPlatform === 'youtube' ? 'Channel Name or Handle' : 'Username'}
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">@</span>
            <input
              type="text"
              value={usernameInput}
              onChange={(e) => { setUsernameInput(e.target.value); setError(null); }}
              onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
              placeholder={platformInfo.placeholder}
              disabled={isConnecting}
              className="w-full pl-8 pr-3 py-2.5 text-xs bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-gray-600 disabled:opacity-50"
              autoFocus
            />
          </div>

          {error && (
            <div className="mt-2 p-2 bg-red-900/30 border border-red-800/50 rounded-lg text-red-400 text-xs">
              {error}
            </div>
          )}

          {isConnecting && (
            <div className="mt-2 p-2 bg-blue-900/30 border border-blue-800/50 rounded-lg text-blue-400 text-xs flex items-center gap-2">
              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {connectionStatus}
            </div>
          )}

          <button
            onClick={handleConnect}
            disabled={isConnecting || !usernameInput.trim()}
            className="w-full mt-4 py-2.5 text-white text-xs font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: FILL_GRADIENT }}
          >
            {isConnecting ? 'Connecting...' : 'Connect'}
          </button>

          {/* Hint */}
          <p className="mt-3 text-gray-500 text-center" style={{ fontSize: '10px' }}>
            {selectedPlatform === 'tiktok' && 'The streamer must be currently live for connection to work'}
            {selectedPlatform === 'youtube' && 'Enter the channel name or @handle of a live stream'}
            {selectedPlatform === 'twitch' && 'The streamer must be currently live for connection to work'}
          </p>

          {/* Discord share toggle */}
          <div className="mt-4 pt-3 border-t border-gray-700/60">
            <button
              onClick={toggleDiscordShare}
              className="w-full flex items-center justify-between gap-3 group relative"
            >
              <div className="absolute bottom-full left-0 right-0 mb-2 px-2.5 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10" style={{ fontSize: '10px' }}>
                Your stream will be announced in the stream channel of the Beemi Discord and a link to your stream will be posted.
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057c.002.022.015.04.033.05a19.834 19.834 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                </svg>
                <span className="text-xs text-gray-300 truncate">Share stream to Beemi discord</span>
              </div>
              <div
                className="relative flex-shrink-0 w-8 h-4 rounded-full transition-all duration-200"
                style={{ background: shareToDiscord ? FILL_GRADIENT : '#4B5563' }}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform duration-200 ${
                    shareToDiscord ? 'translate-x-4' : 'translate-x-0'
                  }`}
                />
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderConnected = () => {
    if (!connectedPlatform) return null;
    const platformInfo = platformLabels[connectedPlatform];

    return (
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div
          className="px-4 py-3 flex items-center justify-between flex-shrink-0"
          style={{ backgroundColor: `${platformInfo.color}20` }}
        >
          <div className="flex items-center gap-2">
            {/* Avatar: profile pic with platform badge, or standalone platform icon */}
            <div className="relative flex-shrink-0">
              {roomInfo?.streamerInfo?.profileImageUrl ? (
                <>
                  <img
                    src={roomInfo.streamerInfo.profileImageUrl}
                    alt={connectedUsername ?? ''}
                    className="w-10 h-10 rounded-full object-cover"
                    style={{ border: `2px solid ${platformInfo.color}` }}
                  />
                  <div
                    className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: platformInfo.color }}
                  >
                    <PlatformIcon platform={connectedPlatform} className="w-2.5 h-2.5 text-white" />
                  </div>
                </>
              ) : (
                <PlatformIcon platform={connectedPlatform} className="w-8 h-8 text-white" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                {isConnecting ? (
                  <>
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24" style={{ color: platformInfo.color }}>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="text-xs font-medium text-white">Connecting...</span>
                  </>
                ) : (
                  <>
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: platformInfo.color }} />
                    <span className="text-xs font-bold text-white">LIVE</span>
                  </>
                )}
              </div>
              <p className="text-gray-300" style={{ fontSize: '10px' }}>@{connectedUsername}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDisconnect}
              className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors text-xs font-medium"
            >
              Disconnect
            </button>
          </div>
        </div>

        {/* Loading State or Stats + Comments */}
        {isConnecting ? (
          <div className="flex-1 flex items-center justify-center overflow-hidden">
            <div className="text-center">
              <svg className="w-12 h-12 mx-auto mb-4 animate-spin" fill="none" viewBox="0 0 24 24" style={{ color: platformInfo.color }}>
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-gray-400 text-sm">{connectionStatus}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Stats — always visible */}
            <div className="px-4 py-3 border-b border-gray-800 flex-shrink-0">
              <StreamStats
                viewerCount={streamingMetrics.viewerCount}
                likeCount={streamingMetrics.likeCount}
                platform={streamingMetrics.platform}
                connectedUsername={connectedUsername}
              />
            </div>

            {/* Comments header with minimize button — always visible */}
            <div className="flex items-center justify-between px-4 pt-3 pb-1 flex-shrink-0">
              <h3 className="font-semibold text-gray-400 uppercase" style={{ fontSize: '10px', letterSpacing: '0.08em' }}>Live Comments</h3>
              <button
                onClick={handleToggleMinimize}
                className="text-gray-500 hover:text-gray-300 transition-colors text-xs"
                title={isMinimized ? 'Expand' : 'Minimize'}
              >
                {isMinimized ? '▼ Expand' : '▲ Minimize'}
              </button>
            </div>

            {/* Comments list — hidden when minimized */}
            {!isMinimized && (
              <div className="flex-1 px-4 pb-4 overflow-hidden min-h-0">
                <LiveCommentsList
                  comments={liveComments}
                  maxHeight="100%"
                />
              </div>
            )}
          </>
        )}

        {/* Footer — always visible when connected */}
        <div className="flex-shrink-0 px-4 py-2 bg-yellow-500/10 border-t border-yellow-500/20 flex items-center gap-2">
          <span className="text-yellow-400 text-xs">⚠</span>
          <p className="text-xs text-yellow-400">Keep this window open to stay connected.</p>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen bg-gray-900 overflow-hidden">
      {view === 'saved-connections' && renderSavedConnections()}
      {view === 'platform-select' && renderPlatformSelect()}
      {view === 'username-input' && renderUsernameInput()}
      {view === 'connected' && renderConnected()}
    </div>
  );
};

export default StreamConnectorPage;

